import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DateSelector from './DateSelector';
import LoveResults from './LoveResults';
import AchievementsDisplay from './AchievementsDisplay';
import MilestonesDisplay from './MilestonesDisplay';
import CollapsibleSection from './CollapsibleSection';
import { calculateDaysDifference, calculateDetailedDifference } from '../utils/dateUtils';
import { LOVE_ACHIEVEMENTS, LOVE_MILESTONES } from '../data/loveData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { parseUrlParams } from '../utils/shareUtils';

const LoveCalculator = ({ language, onDataUpdate }) => {
  const [anniversaryDate, setAnniversaryDate] = useState(null);
  const [anniversaryType, setAnniversaryType] = useState('couple');
  const [customLabel, setCustomLabel] = useState('');
  const [customLabelEn, setCustomLabelEn] = useState('');
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [calculationCount, setCalculationCount] = useState(0);

  useEffect(() => {
    const { date, type } = parseUrlParams();
    if (date) {
      setAnniversaryDate(date);
      setTimeout(() => handleCalculate(date), 300);
    }
    if (type) setAnniversaryType(type);
  }, []);

  const handleCalculate = (date = anniversaryDate) => {
    if (!date) return;

    const today = new Date();
    const days = calculateDaysDifference(date, today);
    if (days < 0) {
      alert(language === 'zh' ? '紀念日期不能是未來的日期' : 'Anniversary date cannot be in the future');
      return;
    }

    setCalculatedDays(days);
    setResultsVisible(true);
    setCalculationCount(prev => prev + 1);

    const relationshipLabel = getRelationshipLabel();
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('date', `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    newUrl.searchParams.set('type', anniversaryType);
    newUrl.searchParams.set('lang', language);
    window.history.replaceState({}, '', newUrl);

    if (onDataUpdate) {
      onDataUpdate({ anniversaryDate: date, anniversaryType, calculatedDays: days, relationshipLabel });
    }
  };

  const getRelationshipLabel = () => {
    if (anniversaryType === 'custom') {
      return language === 'zh' ? (customLabel || '紀念') : (customLabelEn || 'commemorating');
    }
    const labels = {
      couple:       { zh: '在一起',       en: 'together' },
      'first-meet': { zh: '相識',         en: 'knowing each other' },
      'first-date': { zh: '約會',         en: 'dating' },
      proposal:     { zh: '求婚',         en: 'engagement' },
      wedding:      { zh: '結婚',         en: 'married' }
    };
    return labels[anniversaryType]?.[language] || labels.couple[language];
  };

  const getRelationshipTypeIcon = (type) => {
    const icons = { couple: '💑', 'first-meet': '👋', 'first-date': '🍷', proposal: '💍', wedding: '💒', custom: '✨' };
    return icons[type] || '💑';
  };

  const achievements = LOVE_ACHIEVEMENTS[language] || [];
  const detailedTime = anniversaryDate ? calculateDetailedDifference(anniversaryDate, new Date()) : null;

  const relationshipTypes = [
    { id: 'first-meet', icon: '👋', nameZh: '初次相遇', nameEn: 'First Met',    descriptionZh: '記錄你們第一次見面的時刻',     descriptionEn: 'Remember when you first laid eyes on each other' },
    { id: 'first-date', icon: '🍷', nameZh: '第一次約會', nameEn: 'First Date', descriptionZh: '紀念你們第一次約會的美好時光', descriptionEn: 'Commemorate your first romantic date together' },
    { id: 'couple',     icon: '💑', nameZh: '開始交往',   nameEn: 'Dating',     descriptionZh: '記錄你們正式確認關係的日子', descriptionEn: 'Mark the day you officially became a couple' },
    { id: 'proposal',   icon: '💍', nameZh: '求婚紀念',   nameEn: 'Proposal',   descriptionZh: '紀念那個浪漫求婚的特別時刻', descriptionEn: 'Remember that special moment of proposal' },
    { id: 'wedding',    icon: '💒', nameZh: '結婚紀念日', nameEn: 'Wedding',    descriptionZh: '慶祝你們結為連理的幸福日子', descriptionEn: 'Celebrate the day you tied the knot' },
    { id: 'custom',     icon: '✨', nameZh: '自定義紀念日', nameEn: 'Custom',   descriptionZh: '創建屬於你們的特別紀念日',   descriptionEn: 'Create your own special commemoration' }
  ];

  return (
    <div className="p-6 bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Date Selection */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <DateSelector
            language={language}
            selectedDate={anniversaryDate}
            onDateChange={setAnniversaryDate}
            label={language === 'zh' ? '我們的紀念日' : 'Our Anniversary'}
            icon="❤️"
          />

          {/* Relationship Type Selector */}
          <motion.div
            className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center">
              <span className="emoji mr-2">{getRelationshipTypeIcon(anniversaryType)}</span>
              <span className="text-base">{language === 'zh' ? '紀念類型' : 'Relationship Type'}</span>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              {relationshipTypes.map(type => (
                <motion.div
                  key={type.id}
                  onClick={() => {
                    setAnniversaryType(type.id);
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('type', type.id);
                    window.history.replaceState({}, '', newUrl);
                  }}
                  className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                    anniversaryType === type.id
                      ? 'border-love-500 bg-love-50 dark:bg-love-900/30 dark:border-love-400 shadow-sm'
                      : 'border-gray-200 dark:border-gray-700 hover:border-love-300 dark:hover:border-love-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={language === 'zh' ? type.nameZh : type.nameEn}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex flex-col items-center text-center">
                    <span className="emoji text-2xl mb-1">{type.icon}</span>
                    <span className={`text-sm font-medium ${
                      anniversaryType === type.id
                        ? 'text-love-700 dark:text-love-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {language === 'zh' ? type.nameZh : type.nameEn}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={anniversaryType}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="text-xs text-gray-500 dark:text-gray-400 mt-2 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg"
              >
                <div className="flex items-start">
                  <SafeIcon icon={FiIcons.FiInfo} className="mr-1 mt-0.5 flex-shrink-0 text-love-400" />
                  <span>
                    {language === 'zh'
                      ? relationshipTypes.find(t => t.id === anniversaryType)?.descriptionZh
                      : relationshipTypes.find(t => t.id === anniversaryType)?.descriptionEn}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <AnimatePresence>
            {anniversaryType === 'custom' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm"
              >
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300 flex items-center">
                  <span className="emoji mr-1">✨</span>
                  {language === 'zh' ? '自定義標籤' : 'Custom Label'}
                </label>
                <input
                  type="text"
                  value={language === 'zh' ? customLabel : customLabelEn}
                  onChange={(e) => language === 'zh' ? setCustomLabel(e.target.value) : setCustomLabelEn(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 border-love-300 dark:border-love-600 bg-white dark:bg-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-love-500 dark:focus:ring-love-400 transition-colors"
                  placeholder={language === 'zh' ? '例如：第一次約會、求婚...' : 'e.g. First Date, Proposal...'}
                  aria-label={language === 'zh' ? '自定義標籤' : 'Custom Label'}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => handleCalculate()}
            disabled={!anniversaryDate}
            className="w-full bg-gradient-to-r from-love-500 to-love-600 hover:from-love-600 hover:to-love-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-4 rounded-lg transition shadow-sm hover:shadow-md flex items-center justify-center"
            aria-label={language === 'zh' ? '計算我們的時光' : 'Calculate Our Time'}
          >
            <span className="emoji animate-heartbeat mr-2">❤️</span>
            {language === 'zh' ? '計算我們的時光' : 'Calculate Our Time'}
          </button>
        </motion.div>

        {/* Results Display */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <LoveResults
            language={language}
            calculatedDays={calculatedDays}
            detailedTime={detailedTime}
            relationshipLabel={getRelationshipLabel()}
            resultsVisible={resultsVisible}
            anniversaryDate={anniversaryDate}
            anniversaryType={anniversaryType}
            calculationCount={calculationCount}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-6"
      >
        <CollapsibleSection
          title={language === 'zh' ? '愛情成就' : 'Love Achievements'}
          emoji="🏆"
          language={language}
          isInitiallyExpanded={resultsVisible}
        >
          <AchievementsDisplay
            achievements={achievements}
            calculatedDays={calculatedDays}
            language={language}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title={language === 'zh' ? '愛情里程碑' : 'Love Milestones'}
          emoji="🎊"
          language={language}
          isInitiallyExpanded={resultsVisible}
        >
          <MilestonesDisplay
            milestones={LOVE_MILESTONES}
            language={language}
            calculatedDays={calculatedDays}
            anniversaryDate={anniversaryDate}
          />
        </CollapsibleSection>
      </motion.div>
    </div>
  );
};

export default LoveCalculator;